import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Compass, Sparkles, Target, ArrowRight, Users, Building } from 'lucide-react';
import { heroImages, growthImages, coreValues } from '../data/mock';

const HomePage = () => {
  const iconMap = {
    Heart,
    Compass,
    Sparkles,
    Target
  };

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--gradient-hero-warm)',
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '5rem 1.5rem 3rem',
        position: 'relative',
        overflow: 'hidden'
      }} className="!-mt-[75px] !ml-[20px] !mr-[20px] !pt-[50px] !pb-[50px]">
        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(255, 255, 255, 0.4)',
            borderRadius: '2rem',
            padding: '0.25rem 0.75rem',
            marginBottom: '1.5rem',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.025em',
            backdropFilter: 'blur(4px)'
          }}>
            <Sparkles size={14} />
            <span className="font-mono">Wellness-First Business Strategy</span>
          </div>

          <h1 className="heading-hero" style={{ marginBottom: '1.5rem', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
            Building businesses from the inside out
          </h1>
          
          <p className="body-large" style={{ marginBottom: '2rem', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto', color: 'var(--text-secondary)' }}>
            Where wholeness meets strategy — building the clarity, structure & systems that move meaningful visions forward.
          </p>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/products" className="btn-primary" style={{ gap: '0.5rem' }}>
              Explore Services
              <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="btn-secondary">
              Book a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Intersecting Wellness & Business Strategy */}
      <section className="section !mx-[12px]" style={{ background: 'var(--bg-page)' }}>
        <div className="container !ml-[20px] !pt-[10px] !pb-[10px] !pr-[50px]">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '3rem', alignItems: 'center' }}>
            <div>
              <img
                src={growthImages.workspace1}
                alt="Black professional in calm workspace"
                style={{ width: '100%', height: 'auto', borderRadius: '0.75rem', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }} className="!-ml-[55px]" />

            </div>
            <div>
              <h2 className="heading-1 !text-center !-ml-[40px] !mr-[50px] !pl-[15px] !pr-[10px]" style={{ marginBottom: '1.5rem' }}>
                Intersecting Wellness & Business Strategy
              </h2>
              <p className="body-large !text-center !-ml-[40px] !mr-[50px] !text-[#353535]" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                The VEC provides business support rooted in a wellness-first approach. We blend business strategy with human-centered practices.
              </p>
              <p className="body-large !text-center !-ml-[40px] !mr-[50px] !text-[#353535]" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                <strong>When you are well, your business becomes well.</strong>
              </p>
              <Link to="/about" className="btn-primary">
                Book a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Pathways */}
      <section className="section !mx-[12px]" style={{ background: 'var(--bg-section)' }}>
        <div className="container !-ml-[37px] !mr-[10px]">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1 !-mr-[70px]" style={{ marginBottom: '1rem' }}>
              Choose Your Path
            </h2>
            <p className="body-large !-mr-[70px] !text-[#353535]" style={{ color: 'var(--text-secondary)' }}>
              Three pathways to support your entrepreneurial journey
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Path A */}
            <div className="card accent-purple" style={{ padding: '2.5rem' }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'var(--text-primary)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Users size={24} color="white" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
                Path A: Entrepreneurial Wellness Community
              </h3>
              <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                For founders who want to build without burning out. A sacred space for grounding, clarity, connection, and sustainable growth tools.
              </p>
              <Link to="/join-ewc" className="btn-primary">
                Join the EWC
              </Link>
            </div>

            {/* Path B */}
            <div className="card accent-blue" style={{ padding: '2.5rem' }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'var(--text-primary)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Sparkles size={24} color="white" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
                Path B: Aspiring Entrepreneurs
              </h3>
              <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                For those who need to go from <strong>Idea-to-Income</strong>. We'll help you discover, launch, and build your business foundation.
              </p>
              <Link to="/products#launch" className="btn-primary">
                Start Your Journey
              </Link>
            </div>

            {/* Path C */}
            <div className="card accent-orange" style={{ padding: '2.5rem' }}>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'var(--text-primary)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <Building size={24} color="white" />
              </div>
              <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
                Path C: Operational Support
              </h3>
              <p className="body-medium" style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
                For entrepreneurs who need reliable systems, structure, and behind-the-scenes support to grow with ease.
              </p>
              <Link to="/products" className="btn-primary">
                Explore Support Options
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section !-mt-[20px] !ml-[12px] !mr-[12px] !bg-[rgba(255,244,232,0.6)]" style={{ background: 'var(--bg-section)' }}>
        <div className="container !-ml-[15px] !mr-[10px]">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1 !ml-[25px]" style={{ marginBottom: '1rem' }}>
              Our Four Pillars
            </h2>
            <p className="body-large !ml-[25px] !text-[#353535]" style={{ color: 'var(--text-secondary)' }}>
              The foundation of everything we do
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {coreValues.map((value) => {
              const Icon = iconMap[value.icon];
              return (
                <div key={value.id} className="card" style={{ padding: '2rem', textAlign: 'center' }}>
                  <div style={{
                    width: '3.5rem',
                    height: '3.5rem',
                    background: 'var(--accent-grey-200)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                  }}>
                    <Icon size={28} color="var(--text-primary)" />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>
                    {value.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {value.description}
                  </p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section !mx-[12px]" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container !mx-[10px]" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
            Ready to build your business from the inside out?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', color: 'var(--text-secondary)', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
            Let's create your custom growth blueprint together. Strategy and soul, combined.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ gap: '0.5rem' }}>
              Book a Consultation
              <ArrowRight size={16} />
            </Link>
            <Link to="/join-ewc" className="btn-secondary">
              Join The EWC
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default HomePage;